import {Tag} from 'primeng/tag';
import {Component, computed, inject, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {BehaviorSubject, finalize, tap} from 'rxjs';
import {TranslocoDirective, TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {ButtonModule} from 'primeng/button';
import {TableModule} from 'primeng/table';
import {DialogModule} from 'primeng/dialog';
import {ToastModule} from 'primeng/toast';
import {MessageService} from 'primeng/api';
import {MenuModule} from 'primeng/menu';
import {CheckboxModule} from 'primeng/checkbox';
import {
  ProviderRoleResponse,
  ProviderUserControllerService,
  ProviderUserResponse,
  ProviderUserState,
} from '@frontend/shared-api';
import {ToastService} from '@frontend/toast';
import {Card} from 'primeng/card';
import {toSignal} from '@angular/core/rxjs-interop';
import {
  AddEditProviderUserDialogComponent,
  ProviderUserFormData,
} from './add-edit-user-dialog/add-edit-provider-user-dialog.component';
import {switchMap} from 'rxjs/operators';
import {GlobalSearchComponent} from '../../../shared/components/search/global-search/global-search.component';
import {ConfirmationDialogService} from '../../../shared/components/dialog/confirmation/confirmation-dialog.service';

@Component({
  selector: 'app-provider-user-list',
  imports: [
    FormsModule,
    TranslocoDirective,
    TranslocoPipe,
    ButtonModule,
    TableModule,
    DialogModule,
    ToastModule,
    MenuModule,
    CheckboxModule,
    Card,
    AddEditProviderUserDialogComponent,
    GlobalSearchComponent,
    Tag,
  ],
  providers: [MessageService],
  templateUrl: './provider-user-list.component.html',
})
export class ProviderUserListComponent {
  private readonly providerUserService = inject(ProviderUserControllerService);
  private readonly toast = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);

  loading = signal(false);

  private readonly refreshTrigger$ = new BehaviorSubject<void>(undefined);

  private readonly users$ = this.refreshTrigger$.pipe(
    tap(() => this.loading.set(true)),
    switchMap(() =>
      this.providerUserService.getAllProviderUsers().pipe(finalize(() => this.loading.set(false))),
    ),
  );

  users = toSignal(this.users$, {
    initialValue: [] as ProviderUserResponse[],
  });
  selectedUser = signal<ProviderUserResponse | null>(null);

  emailEditingDisabled = computed(
    () =>
      !!this.selectedUser() &&
      this.isEditMode() &&
      this.selectedUser()?.state !== ProviderUserState.CREATED &&
      this.selectedUser()?.state !== ProviderUserState.INVITED,
  );

  editUserDialogVisible = signal(false);
  readonly availableRoles = toSignal(this.providerUserService.loadAllAvailableRoles(), {
    initialValue: [] as ProviderRoleResponse[],
  });

  globalFilterFields = ['firstName', 'lastName', 'email', 'role'];

  loadUsers() {
    this.refreshTrigger$.next();
  }

  onSubmit(form: ProviderUserFormData) {
    if (!form.role?.role) {
      this.toast.showError('common.error', 'errors.roleRequired');
      return;
    }
    this.loading.set(true);
    const userRequest = {
      userUuid: this.selectedUser()?.uuid,
      email: form.email!,
      firstName: form.firstName!,
      lastName: form.lastName!,
      phoneNumber: form.phoneNumber!,
      role: form.role.role!,
      languageCode: this.translocoService.getActiveLang(),
    };
    const providerUserResponseObservable = this.selectedUser()?.uuid
      ? this.providerUserService.updateProviderUser({
          userUuid: this.selectedUser()!.uuid!,
          createUpdateProviderUserRequest: userRequest,
        })
      : this.providerUserService.createProviderUser({
          createUpdateProviderUserRequest: userRequest,
        });
    providerUserResponseObservable
      .pipe(
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.toast.showSuccess('success.userAdded');
          this.loadUsers();
          this.editUserDialogVisible.set(false);
          this.selectedUser.set(null);
        },
        error: () => {
          this.toast.showError('common.error', 'error.generic.detail');
        },
      });
  }

  handleEditUser(user?: ProviderUserResponse) {
    if (user) {
      this.selectedUser.set({...user});
    } else {
      // Set to an empty object instead of null to ensure AbstractFormDirective patches with defaults
      this.selectedUser.set({} as ProviderUserResponse);
    }
    this.editUserDialogVisible.set(true);
  }

  handleDeleteUser(user: ProviderUserResponse): void {
    if (!user?.uuid) {
      this.toast.showError('common.error', 'error.generic.detail');
      return;
    }

    const translationPrefix = `provider.user.confirm.delete`;

    const headerKey = translationPrefix + '.header';
    const messageKey = translationPrefix + '.message';
    const userName = user.firstName + ' ' + user.lastName;

    this.confirmDialogService.confirm({
      headerKey: headerKey,
      messageKey: messageKey,
      messageParams: {name: userName},
      accept: () => {
        this.performDeleteUser(user);
      },
    });
  }

  performDeleteUser(user: ProviderUserResponse) {
    this.loading.set(true);
    this.providerUserService
      .deleteProviderUser({
        userUuid: user.uuid!,
      })
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.editUserDialogVisible.set(false);
          this.selectedUser.set(null);
        }),
      )
      .subscribe({
        next: () => {
          this.toast.showSuccess('success.userUpdated');
          this.loadUsers();
          this.editUserDialogVisible.set(false);
          this.selectedUser.set(null);
        },
        error: () => {
          this.toast.showError('common.error', 'error.generic.detail');
        },
      });
  }

  getRoleName(role: ProviderRoleResponse) {
    return role.translations?.filter(
      (trans) => this.translocoService.getActiveLang() === trans.language,
    )[0].name;
  }

  protected isEditMode() {
    return this.selectedUser()?.id !== undefined;
  }
}
